import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, MapPin, Anchor, HandCoins, ShieldCheck, Fish, ArrowRight, PlusCircle, Eye, FormInput, ShieldCheck as ShieldCheck2, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import TripCard from "../components/TripCard";
import PortsMap from "../components/PortsMap";
import WeatherWidget from "../components/WeatherWidget";

export default function Home() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [port, setPort] = useState("");

  const { data: trips = [] } = useQuery({
    queryKey: ["trips-home"],
    queryFn: async () => (await api.get("/trips")).data,
  });
  const { data: ports = [] } = useQuery({
    queryKey: ["ports"],
    queryFn: async () => (await api.get("/ports")).data,
  });

  useEffect(() => { document.title = "Le Bon Matelot — embarquer avec d'autres pêcheurs"; }, []);

  // Pick ONE example trip — prefer Bréhec, with spots left
  const example = useMemo(() => {
    return trips.find((t) => t.port === "Bréhec" && t.spots_left > 0)
      || trips.find((t) => t.spots_left > 0)
      || trips[0];
  }, [trips]);

  const onSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (port) params.set("port", port);
    nav(`/explorer?${params.toString()}`);
  };

  return (
    <div>
      {/* ─── HERO ─── */}
      <section className="bg-gradient-to-b from-brume/60 to-blanc border-b border-brume">
        <div className="max-w-5xl mx-auto px-6 pt-12 pb-10 md:pt-16 md:pb-12">
          <div className="inline-flex items-center gap-2 bg-blanc px-3 py-1.5 rounded-full text-[11px] font-bold text-bretagne uppercase tracking-widest border border-brume">
            <Fish className="w-3 h-3" /> Pêche en mer · Bretagne
          </div>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-ocean leading-[1.1] tracking-tight max-w-3xl">
            Trouvez une place à bord <br className="hidden md:block" />
            <span className="text-bretagne">pour votre prochaine partie de pêche.</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-ocean/75 max-w-2xl leading-relaxed">
            Partagez les frais et embarquez avec d'autres passionnés.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Button
              data-testid="hero-cta-find"
              onClick={() => nav("/explorer")}
              className="bg-blanc border-2 border-ocean text-ocean hover:bg-ocean hover:text-blanc font-bold rounded-full px-6 py-5 tactile"
            >
              <Eye className="mr-2 w-4 h-4" /> Voir les sorties
            </Button>
            <Button
              data-testid="hero-cta-publish"
              onClick={() => nav("/publier")}
              className="bg-ocean hover:bg-ocean/90 text-blanc font-bold rounded-full px-6 py-5 tactile shadow-matelot-lg"
            >
              <PlusCircle className="mr-2 w-4 h-4" /> Proposer une sortie
            </Button>
          </div>

          {/* Search bar */}
          <form
            onSubmit={onSearch}
            className="mt-7 bg-blanc rounded-2xl border border-brume shadow-matelot p-2 flex flex-col md:flex-row gap-2"
          >
            <div className="flex-1 flex items-center gap-3 px-4 py-2.5">
              <Search className="w-5 h-5 text-bretagne shrink-0" />
              <Input
                data-testid="hero-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Type de pêche, port, mot-clé…"
                className="border-0 shadow-none p-0 focus-visible:ring-0 text-base placeholder:text-ocean/45"
              />
            </div>
            <div className="md:w-52 flex items-center gap-2 px-4 py-2.5 bg-brume/40 rounded-xl">
              <MapPin className="w-4 h-4 text-bretagne shrink-0" />
              <select
                data-testid="hero-search-port"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                className="w-full bg-transparent text-sm font-semibold text-ocean focus:outline-none"
              >
                <option value="">Tous les ports</option>
                {ports.map((p) => (<option key={p.id} value={p.name}>{p.name}</option>))}
              </select>
            </div>
            <Button
              data-testid="hero-search-submit"
              type="submit"
              className="bg-ocean hover:bg-ocean/90 text-blanc font-bold rounded-xl px-6 tactile"
            >
              Rechercher
            </Button>
          </form>
        </div>
      </section>

      {/* ─── Sortie EXEMPLE ─── */}
      {example && (
        <section className="max-w-5xl mx-auto px-6 pt-12">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-corail font-bold">Sortie exemple</div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-ocean mt-1">
                À quoi ressemble une sortie ?
              </h2>
            </div>
            <Link to="/explorer" data-testid="home-see-all" className="text-bretagne font-bold text-sm inline-flex items-center gap-1.5 hover:text-corail">
              Toutes les sorties <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4 items-start">
            <div className="md:col-span-2">
              <TripCard trip={example} />
            </div>
            <div className="bg-sable border border-brume rounded-2xl p-5 text-sm text-ocean/80 leading-relaxed">
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-bretagne font-bold mb-2">
                <Fish className="w-3 h-3" /> Comment lire une annonce
              </div>
              <p>
                Voici un exemple de sortie proposée par un membre. Vous y voyez le port,
                la date, la durée, le nombre de places et la <strong>participation aux frais</strong>{" "}
                (carburant, appâts, consommables).
              </p>
              <p className="mt-2">
                Aucun paiement sur la plateforme — le règlement se fait directement entre les membres.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ─── Tutoriel publier ─── */}
      <section className="max-w-5xl mx-auto px-6 pt-14">
        <div className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Tutoriel</div>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-ocean mt-1">
          Comment publier une sortie&nbsp;?
        </h2>
        <p className="mt-1 text-ocean/70 text-sm">En 4 étapes, pas de mot de passe, c'est immédiat.</p>

        <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { n: "1", t: "Je clique", d: "« Proposer une sortie »", icon: PlusCircle },
            { n: "2", t: "Je remplis", d: "Mon annonce (port, date, places, frais…)", icon: FormInput },
            { n: "3", t: "Je valide", d: "Mon téléphone ou mon email avec un code", icon: ShieldCheck2 },
            { n: "4", t: "C'est en ligne", d: "Mon annonce est publiée immédiatement", icon: CheckCircle2 },
          ].map((s) => (
            <div
              key={s.n}
              data-testid={`tuto-step-${s.n}`}
              className="bg-blanc border border-brume rounded-2xl p-4"
            >
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-full bg-ocean text-jaune font-display font-bold grid place-items-center text-sm">
                  {s.n}
                </span>
                <s.icon className="w-5 h-5 text-corail" strokeWidth={1.8} />
              </div>
              <div className="mt-3 font-display text-base font-bold text-ocean">{s.t}</div>
              <div className="text-xs text-ocean/70 mt-1 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex justify-center">
          <Button
            data-testid="tuto-cta-publish"
            onClick={() => nav("/publier")}
            className="bg-ocean hover:bg-ocean/90 text-blanc font-bold rounded-full px-7 py-5 tactile shadow-matelot"
          >
            <PlusCircle className="w-4 h-4 mr-2" /> Proposer ma sortie maintenant
          </Button>
        </div>
      </section>

      {/* ─── Carte + Météo ─── */}
      <section className="max-w-5xl mx-auto px-6 pt-14 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-widest text-bretagne font-bold mb-2">
            Nos ports
          </div>
          <PortsMap ports={ports} trips={trips} height={340} />
          <p className="text-[11px] text-bretagne/70 mt-2">
            Cliquez sur un port pour voir les sorties disponibles.
          </p>
        </div>
        <div>
          <WeatherWidget defaultPort="brehec" />
        </div>
      </section>

      {/* ─── Sécurité + Partage des frais ─── */}
      <section className="max-w-5xl mx-auto px-6 mt-12 mb-16 grid md:grid-cols-2 gap-3">
        <div className="bg-brume/50 border border-brume rounded-2xl p-5">
          <ShieldCheck className="w-7 h-7 text-bretagne" />
          <h3 className="font-display text-lg font-bold text-ocean mt-2">Sécurité</h3>
          <p className="text-sm text-ocean/75 mt-1 leading-relaxed">
            Comptes vérifiés par téléphone ou email. Notes et avis transparents.
          </p>
        </div>
        <div className="bg-sable border border-brume rounded-2xl p-5">
          <HandCoins className="w-7 h-7 text-corail" />
          <h3 className="font-display text-lg font-bold text-ocean mt-2">Partage des frais — pas un commerce</h3>
          <p className="text-sm text-ocean/75 mt-1 leading-relaxed">
            La participation est <strong className="text-ocean">fixée librement par le capitaine</strong>{" "}
            et reste indicative. Aucun paiement sur Le Bon Matelot, tout se règle en main propre.
          </p>
        </div>
      </section>
    </div>
  );
}
